import type {
  ApplyActionCounts,
  ApplyClassification,
  ApplyDiagnostic,
  ApplyReasonCode,
  ApplyResultSummary,
} from "../types";

const MAX_DIAGNOSTIC_ISSUES = 60;

export function createActionCounts(): ApplyActionCounts {
  return {
    create: 0,
    modify: 0,
    delete: 0,
    duplicate: 0,
  };
}

function incrementReasonCount(
  bucket: Partial<Record<ApplyReasonCode, number>>,
  code: ApplyReasonCode,
  delta = 1
): void {
  bucket[code] = (bucket[code] || 0) + delta;
}

export function recordSkipped(
  summary: ApplyResultSummary,
  issue: ApplyDiagnostic,
  count = 1
): void {
  summary.skipped.count += count;
  incrementReasonCount(summary.skipped.byReason, issue.code, count);
  if (summary.skipped.issues.length < MAX_DIAGNOSTIC_ISSUES) {
    summary.skipped.issues.push(issue);
  }
}

export function recordError(
  summary: ApplyResultSummary,
  issue: ApplyDiagnostic,
  count = 1
): void {
  summary.errors.count += count;
  incrementReasonCount(summary.errors.byReason, issue.code, count);
  if (summary.errors.issues.length < MAX_DIAGNOSTIC_ISSUES) {
    summary.errors.issues.push(issue);
  }
}

export function countSuccessfulOps(summary: ApplyResultSummary): number {
  const estimatedShapeDeleteOps = Math.max(
    0,
    summary.applied.shapes - summary.applied.byAction.create - summary.applied.byAction.modify
  );
  const slideDeleteOps = Math.max(0, summary.applied.byAction.delete - estimatedShapeDeleteOps);
  return summary.applied.shapes + summary.applied.byAction.duplicate + slideDeleteOps;
}

export function classifyApplyResult(summary: ApplyResultSummary): ApplyClassification {
  const successfulOps = countSuccessfulOps(summary);

  if (successfulOps > 0) {
    if (summary.skipped.count > 0 || summary.errors.count > 0) {
      return "partial_apply";
    }
    return "applied";
  }

  const hasTargetMismatch = Boolean(
    summary.skipped.byReason.SLIDE_INDEX_OUT_OF_RANGE ||
      summary.skipped.byReason.SHAPE_ID_NOT_FOUND ||
      summary.skipped.byReason.PARAGRAPH_TARGET_INDEX_OUT_OF_RANGE
  );
  const hasModelOutputMismatch = Boolean(
    summary.skipped.byReason.MODEL_OUTPUT_MISSING_SLIDES ||
      summary.skipped.byReason.MODEL_OUTPUT_PARSE_FALLBACK_EMPTY ||
      summary.skipped.byReason.MODEL_OUTPUT_INVALID_PARAGRAPH_OP ||
      summary.skipped.byReason.MODEL_OUTPUT_INVALID_NATIVE_CHART ||
      summary.skipped.byReason.MODEL_OUTPUT_CHART_OPTION_DROPPED ||
      summary.skipped.byReason.MODEL_OUTPUT_CHART_RISK_REJECTED ||
      summary.skipped.byReason.NO_MUTATION_FIELDS ||
      summary.skipped.issues.some((issue) => issue.category === "model_output_mismatch")
  );
  const hasApplyFailures =
    summary.errors.count > 0 ||
    summary.skipped.issues.some((issue) => issue.category === "apply_error");

  if (hasTargetMismatch) {
    return "target_mismatch";
  }
  if (hasApplyFailures) {
    return "apply_failed";
  }
  if (hasModelOutputMismatch) {
    return "model_output_mismatch";
  }
  return "true_no_op";
}
